/**
 * Columns: a search, and the chosen columns as a strip of chips.
 *
 * Choosing a column is a search — type, pick, and it is on the report. There is no list of every
 * column to scroll through and no grouped chips to hunt in. What has been chosen is a compact
 * strip beneath the search: drag a chip, or use its arrows, to reorder; its cross removes it.
 */
import { useRef, useState } from 'react'
import clsx from 'clsx'
import { ChevronLeft, ChevronRight, Columns3, X } from 'lucide-react'
import { COLUMN_DEFS, COLUMN_MAP } from '../columns'
import { ColumnSearchPicker } from '../ColumnSearchPicker'
import { columnGroups } from '../studioColumns'
import type { ColumnId } from '../types'
import { DeckPanel } from './controls'

export function ColumnsPanel({
  columns,
  active,
  onAdd,
  onRemove,
  onMove,
  onReorder,
  onMatchScreen,
  onResetDefault,
}: {
  columns: ColumnId[]
  active: boolean
  onAdd: (ids: ColumnId[]) => void
  onRemove: (id: ColumnId) => void
  onMove: (id: ColumnId, delta: number) => void
  /** Drop the dragged chip where the target chip is. */
  onReorder: (sourceId: ColumnId, targetId: ColumnId) => void
  onMatchScreen: () => void
  onResetDefault: () => void
}) {
  const [dragId, setDragId] = useState<ColumnId | null>(null)
  const [dragOverId, setDragOverId] = useState<ColumnId | null>(null)
  // The ref mirrors the dragged id so a drop never depends on a React state flush.
  const dragIdRef = useRef<ColumnId | null>(null)

  const endDrag = () => {
    dragIdRef.current = null
    setDragId(null)
    setDragOverId(null)
  }

  return (
    <DeckPanel
      id="columns"
      title="Columns"
      icon={<Columns3 className="h-3.5 w-3.5" />}
      active={active}
      meta={<span className="tabular-nums">{columns.length} on the report</span>}
    >
      <ColumnSearchPicker
        variant="compact"
        options={COLUMN_DEFS}
        selected={columns}
        groups={columnGroups()}
        onAdd={onAdd}
      />
      <ul
        aria-label="Report columns, in printing order"
        className="flex max-h-48 flex-wrap content-start gap-1 overflow-y-auto"
      >
        {columns.map((id, index) => {
          const def = COLUMN_MAP.get(id)
          if (!def) return null
          return (
            <li
              key={id}
              draggable
              onDragStart={() => {
                dragIdRef.current = id
                setDragId(id)
              }}
              onDragEnd={endDrag}
              onDragOver={(event) => {
                event.preventDefault()
                setDragOverId(id)
              }}
              onDrop={(event) => {
                event.preventDefault()
                const source = dragIdRef.current
                if (source && source !== id) onReorder(source, id)
                endDrag()
              }}
              className={clsx(
                'group flex cursor-grab items-center gap-0.5 rounded border py-0.5 pl-1.5 pr-0.5 text-[10px] active:cursor-grabbing',
                dragOverId === id && dragId !== id
                  ? 'border-amber-300/60 bg-amber-300/[0.08] text-amber-100'
                  : 'border-white/[0.08] bg-black/30 text-stone-200 hover:border-amber-300/25',
              )}
            >
              <span className="font-mono text-[8px] text-stone-600">{index + 1}</span>
              <span className="ml-0.5 max-w-[9rem] truncate" title={def.label}>
                {def.label}
              </span>
              {/* Left and right, because the order reads across the report. They stay in the tab
                  order but take no room until the chip is hovered or focused. */}
              <button
                type="button"
                onClick={() => onMove(id, -1)}
                disabled={index === 0}
                className="w-0 overflow-hidden rounded p-0 text-stone-500 opacity-0 transition-all hover:text-amber-200 focus:w-3.5 focus:p-0.5 focus:opacity-100 group-hover:w-3.5 group-hover:p-0.5 group-hover:opacity-100 disabled:hidden"
                aria-label={`Move ${def.label} earlier`}
              >
                <ChevronLeft className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => onMove(id, 1)}
                disabled={index === columns.length - 1}
                className="w-0 overflow-hidden rounded p-0 text-stone-500 opacity-0 transition-all hover:text-amber-200 focus:w-3.5 focus:p-0.5 focus:opacity-100 group-hover:w-3.5 group-hover:p-0.5 group-hover:opacity-100 disabled:hidden"
                aria-label={`Move ${def.label} later`}
              >
                <ChevronRight className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => onRemove(id)}
                disabled={columns.length === 1}
                className="rounded p-0.5 text-stone-500 hover:text-red-300 disabled:opacity-30"
                aria-label={`Remove ${def.label}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </li>
          )
        })}
      </ul>
      <div className="flex items-center gap-3 text-[10px] text-stone-500">
        <button
          type="button"
          onClick={onMatchScreen}
          className="hover:text-amber-200"
          title="Reset columns and sort to what the workspace grid is showing"
        >
          Match screen
        </button>
        <button type="button" onClick={onResetDefault} className="hover:text-amber-200">
          Reset default
        </button>
      </div>
    </DeckPanel>
  )
}
